import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { employeeService } from '../services/employeeService';
import { EmployeeRecord } from '../types';
import { useAuth } from '../context/AuthContext';
import { FiCopy, FiExternalLink, FiRefreshCcw, FiSearch, FiUserCheck, FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight } from 'react-icons/fi';
import { TableSkeleton, LoadingPill } from '../components/Skeleton';
import { useMinDelayed } from '../hooks/useMinDelayed';

const DraftsPage: React.FC = () => {
  const { user } = useAuth();
  const [drafts, setDrafts] = useState<EmployeeRecord[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Temp filter states (for input fields)
  const [tempSearch, setTempSearch] = useState('');
  const [tempShowMine, setTempShowMine] = useState(true);

  // Actual filter states (for API calls)
  const [search, setSearch] = useState('');
  const [showMine, setShowMine] = useState(true);

  // Pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  const draftLinkBase = useMemo(() => window.location.origin, []);
  const showSkeleton = useMinDelayed(loading, 500);

  const handleSearch = () => {
    setPage(1); // Reset to first page when searching
    setSearch(tempSearch);
    setShowMine(tempShowMine);
  };

  const handleClearFilters = () => {
    setTempSearch('');
    setTempShowMine(false);
    setSearch('');
    setShowMine(false);
    setPage(1);
  };

  const loadDrafts = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const filters: Record<string, any> = { status: ['draft'] };
      if (search) filters.searchTerm = search;

      // Backend now uses Query with GSI - gets ALL matching records efficiently
      const response = await employeeService.getEmployees({
        page,
        limit,
        filters,
      });

      if (response.success && response.data?.data) {
        setDrafts(response.data.data);
        setTotalRecords(response.data.pagination?.total || 0);
      } else {
        toast.error(response.error || 'Failed to load drafts');
      }
    } catch (error) {
      console.error('Failed to load drafts:', error);
      toast.error('Unable to load drafts at the moment.');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    loadDrafts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, user?.id]);

  const copyDraftLink = (draftId: string) => {
    const url = `${draftLinkBase}/register?draftId=${draftId}`;
    navigator.clipboard.writeText(url);
    toast.success('Draft link copied to clipboard');
  };

  const renderOwner = (draft: EmployeeRecord) => {
    if (draft.createdByName) return draft.createdByName;
    if (draft.createdBy === 'candidate') return 'Candidate';
    return draft.createdBy || 'Unknown';
  };

  const filteredDrafts = useMemo(() => {
    if (!showMine || !user?.id) {
      return drafts;
    }
    return drafts.filter((draft) =>
      draft.createdBy === user.id ||
      draft.updatedBy === user.id
    );
  }, [drafts, showMine, user?.id]);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Drafts</h1>
          <p className="text-gray-600 mt-1">
            Manage in-progress applications. Use the toggle to focus on drafts you created.
          </p>
          <div className="mt-2 flex items-center gap-3">
            {showSkeleton ? (
              <LoadingPill label="Loading drafts" />
            ) : (
              <span className="text-sm text-gray-500">
                {filteredDrafts.length} draft{filteredDrafts.length === 1 ? '' : 's'} found
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              setIsRefreshing(true);
              loadDrafts();
            }}
            className="inline-flex items-center px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <FiRefreshCcw className={`mr-2 h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg p-4">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="show-mine"
              className="h-4 w-4 text-blue-600 border-gray-300 rounded"
              checked={tempShowMine}
              onChange={() => setTempShowMine(!tempShowMine)}
            />
            <label htmlFor="show-mine" className="text-sm text-gray-700 flex items-center gap-2">
              <FiUserCheck />
              Show only my drafts
            </label>
          </div>

          <div className="flex-1 flex items-center gap-2">
            <div className="relative flex-1 md:max-w-md">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={tempSearch}
                onChange={(e) => setTempSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search by name or email"
                className="w-full border border-gray-300 rounded-lg pl-10 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={handleSearch}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
            >
              Search
            </button>
            <button
              onClick={handleClearFilters}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors text-sm"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {showSkeleton ? (
        <TableSkeleton rows={limit} cols={4} />
      ) : (
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Candidate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Owner
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredDrafts.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-10 text-center text-gray-500">
                    No drafts found.
                  </td>
                </tr>
              ) : (
                filteredDrafts.map((draft) => {
                  const draftLink = `/register?draftId=${draft.id}`;
                  return (
                    <tr key={draft.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {draft.fullNames || 'Pending'} {draft.surname || ''}
                        </div>
                        <div className="text-sm text-gray-500 break-all">{draft.email || 'No email yet'}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 whitespace-nowrap">
                        {new Date(draft.createdAt).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {renderOwner(draft)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        <div className="flex items-center gap-3">
                          <Link
                            to={draftLink}
                            className="inline-flex items-center text-blue-600 hover:text-blue-800"
                            title="Open draft"
                          >
                            <FiExternalLink className="mr-1 h-4 w-4" />
                            Open
                          </Link>
                          <button
                            onClick={() => copyDraftLink(draft.id)}
                            className="inline-flex items-center text-gray-600 hover:text-gray-800"
                          >
                            <FiCopy className="mr-1 h-4 w-4" />
                            Copy link
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalRecords > 0 && (
          <div className="flex items-center justify-between p-4 border-t border-gray-200">
            <span className="text-sm text-gray-700">
              Page <strong>{page}</strong> of <strong>{Math.ceil(totalRecords / limit)}</strong> | Total Records: {totalRecords}
            </span>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setPage(1)}
                disabled={page === 1}
                className="p-2 border rounded disabled:opacity-50 hover:bg-gray-50"
              >
                <FiChevronsLeft />
              </button>
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="p-2 border rounded disabled:opacity-50 hover:bg-gray-50"
              >
                <FiChevronLeft />
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page >= Math.ceil(totalRecords / limit)}
                className="p-2 border rounded disabled:opacity-50 hover:bg-gray-50"
              >
                <FiChevronRight />
              </button>
              <button
                onClick={() => setPage(Math.ceil(totalRecords / limit))}
                disabled={page >= Math.ceil(totalRecords / limit)}
                className="p-2 border rounded disabled:opacity-50 hover:bg-gray-50"
              >
                <FiChevronsRight />
              </button>
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default DraftsPage;
